<template>
  <v-layout :id="id" class="card-photos">
    <template v-if="reinspectionWithPhotos">
      <v-flex xs12 class="reinspection-photos">
        <v-layout wrap>
          <v-flex
            xs6
            half
            @click="openGallery({ index: 0, photo: originalPhotos[0] })"
          >
            <div
              class="reinspection-thumb"
              :style="{
                'background-image': `url(${thumbUrl(originalPhotos[0])})`,
              }"
            >
              <div v-if="originalPhotos.length > 1" class="photo-count-badge">
                +{{ originalPhotos.length - 1 }}
              </div>
            </div>
            <div class="reinspection-label">Original</div>
          </v-flex>
          <v-flex xs6 half @click="openGallery({ index: 0, photo: photos[0] })">
            <div
              class="reinspection-thumb"
              :style="{ 'background-image': `url(${thumbUrl(photos[0])})` }"
            >
              <div v-if="photos.length > 1" class="photo-count-badge">
                +{{ photos.length - 1 }}
              </div>
            </div>
            <div class="reinspection-label reinspection-label--reinspection">
              Reinspection
            </div>
          </v-flex>
        </v-layout>
      </v-flex>
    </template>
    <template v-else-if="isReinspectionNoOriginal">
      <v-flex xs12 class="reinspection-photos">
        <v-layout wrap>
          <v-flex
            xs12
            half
            @click="openGallery({ index: 0, photo: photos[0] })"
          >
            <div
              class="reinspection-thumb"
              :style="{ 'background-image': `url(${thumbUrl(photos[0])})` }"
            >
              <div v-if="photos.length > 1" class="photo-count-badge">
                +{{ photos.length - 1 }}
              </div>
            </div>
            <div class="reinspection-label reinspection-label--reinspection">
              Reinspection Photos
            </div>
          </v-flex>
        </v-layout>
      </v-flex>
    </template>
    <template v-else>
      <v-flex
        :class="{
          xs12: photoCount === 1,
          'xs6 half': photoCount === 2,
          xs8: photoCount > 2,
        }"
      >
        <photo :photo="photos[0]" :photo-index="0" @openGallery="openGallery" />
      </v-flex>
      <v-flex
        v-if="photoCount > 1"
        :class="{
          'xs6 half': photoCount === 2,
          'xs4 additional-photos': photoCount > 2,
        }"
      >
        <photo
          v-if="photoCount === 2"
          :photo="photos[1]"
          :photo-index="1"
          @openGallery="openGallery"
        />
        <template v-else>
          <v-flex xs12 class="additional-photo">
            <photo
              :photo="photos[1]"
              :photo-index="1"
              @openGallery="openGallery"
            />
          </v-flex>
          <v-flex v-if="photoCount > 2" xs12 class="additional-photo">
            <photo
              :photo="photos[2]"
              :photo-index="2"
              @openGallery="openGallery"
            />
          </v-flex>
          <v-flex
            v-if="photoCount > 3"
            xs12
            class="more additional-photo"
            @click="openGallery({ index: 3, photo: null })"
          >
            + {{ photoCount - 3 }} more
          </v-flex>
        </template>
      </v-flex>
    </template>
  </v-layout>
</template>

<script>
import { mapState } from 'vuex'
import dig from '../../utils/Dig'
import Photo from './Photo.vue'
import { buildGalleryItem } from '@/utils'
import { resolveThumbUrl } from '@/utils/photoUrlResolver'
import EventBus from '@/services/EventBus'
import config from '@/utils/config'
import { buildVideoReleaseListeners } from '@/utils/galleryVideos'

const LIGHT_GALLERY_KEY = config('LIGHT_GALLERY_KEY')

export default {
  components: {
    Photo,
  },
  // eslint-disable-next-line vue/require-prop-types
  props: ['photos', 'id'],
  computed: {
    ...mapState('reports', ['reportView', 'originalReportPhotos']),
    photoCount() {
      return this.photos.length
    },
    originalPhotos() {
      const sourceIds = this.photos.map((p) => p.attributes.source_id)

      if (this.originalReportPhotos && this.originalReportPhotos.length > 0) {
        const matched = this.originalReportPhotos.filter((p) =>
          sourceIds.includes(p.id)
        )
        if (matched.length > 0) return matched
      }

      const reportViewPhotos =
        dig(this.reportView, 'attributes.observation_photos.data') || []
      return reportViewPhotos.filter((p) => sourceIds.includes(p.id))
    },
    reinspectionWithPhotos() {
      return (
        dig(this.reportView, 'attributes.type') === 'ReinspectionView' &&
        this.photos.find((p) => p.attributes.source_id) &&
        this.originalPhotos.length > 0
      )
    },
    isReinspectionNoOriginal() {
      return (
        dig(this.reportView, 'attributes.type') === 'ReinspectionView' &&
        this.photos.length > 0 &&
        this.originalPhotos.length === 0
      )
    },
  },
  mounted() {
    EventBus.$on('closeGallery', this.closeGallery)

    this.$galleryId = document.getElementById(this.id)
    if (!this.$galleryId || this.reinspectionWithPhotos) return

    this.$galleryId.addEventListener('lgAfterClose', this.hidePanoButton)
    this.$handleSlide = (event) => {
      const { index } = event.detail
      const record = this.photos[index]

      if (record.attributes.is_pano) {
        this.$store.commit('reports/setValue', {
          key: 'currentPanoPhoto',
          value: record,
        })
        this.$store.commit('reports/setValue', {
          key: 'showPanoButton',
          value: true,
        })
        return
      }

      this.$store.commit('reports/setValue', {
        key: 'currentPanoPhoto',
        value: null,
      })
      this.$store.commit('reports/setValue', {
        key: 'showPanoButton',
        value: false,
      })
    }
    this.$galleryId.addEventListener('lgAfterSlide', this.$handleSlide)

    const release = buildVideoReleaseListeners(() => this.$gallery)
    this.$releaseOnSlide = release.onBeforeSlide
    this.$releaseOnClose = release.onBeforeClose
    this.$galleryId.addEventListener('lgBeforeSlide', this.$releaseOnSlide)
    this.$galleryId.addEventListener('lgBeforeClose', this.$releaseOnClose)

    this.$gallery = window.lightGallery(this.$galleryId, {
      dynamic: true,
      zoom: true,
      licenseKey: LIGHT_GALLERY_KEY,
      plugins: [window.lgZoom, window.lgVideo, window.lgThumbnail],
      dynamicEl: this.buildDynamicGallery(),
    })
  },
  beforeDestroy() {
    EventBus.$off('closeGallery', this.closeGallery)
    if (this.$galleryId) {
      this.$galleryId.removeEventListener('lgAfterClose', this.hidePanoButton)
      if (this.$handleSlide) {
        this.$galleryId.removeEventListener('lgAfterSlide', this.$handleSlide)
      }
      if (this.$releaseOnSlide) {
        this.$galleryId.removeEventListener(
          'lgBeforeSlide',
          this.$releaseOnSlide
        )
      }
      if (this.$releaseOnClose) {
        this.$galleryId.removeEventListener(
          'lgBeforeClose',
          this.$releaseOnClose
        )
      }
    }
    if (this.$gallery) {
      this.$gallery.destroy()
    }
  },
  methods: {
    thumbUrl(photo) {
      return resolveThumbUrl(photo)
    },
    hidePanoButton() {
      this.$store.commit('reports/setValue', {
        key: 'showPanoButton',
        value: false,
      })
    },
    closeGallery() {
      if (!this.$gallery) return

      this.$gallery.closeGallery(true)
    },
    openGallery({ index, photo }) {
      if (this.reinspectionWithPhotos) {
        if (photo && photo.attributes.is_pano) {
          return this.$store.commit('reports/setValue', {
            key: 'overlayPhoto',
            value: photo,
          })
        }
        return this.$store.dispatch('reports/openComparisonViewer', {
          reinspectionPhotos: this.photos,
          originalPhotos: this.originalPhotos,
          initialReinspectionIndex: index || 0,
          initialOriginalIndex: 0,
        })
      }

      if (!this.$gallery) return

      if (!photo) {
        return this.$gallery.openGallery(index || 0)
      }

      if (photo.attributes.is_pano) {
        return this.$store.commit('reports/setValue', {
          key: 'overlayPhoto',
          value: photo,
        })
      }

      return this.$gallery.openGallery(index || 0)
    },
    photosWithOriginals() {
      const photos = []
      const processedOriginalIds = new Set()
      const newPhotosByOriginal = {}
      const photosWithoutOriginal = []

      this.photos.forEach((photo) => {
        const sourceId = photo.attributes.source_id
        if (!sourceId) {
          photosWithoutOriginal.push(photo)
          return
        }
        if (!newPhotosByOriginal[sourceId]) {
          newPhotosByOriginal[sourceId] = []
        }
        newPhotosByOriginal[sourceId].push(photo)
      })

      this.photos.forEach((photo) => {
        const sourceId = photo.attributes.source_id
        if (!sourceId || processedOriginalIds.has(sourceId)) return

        const original = this.originalPhotos.find(
          (p) => p.attributes.id === sourceId
        )
        if (original) {
          photos.push(original)
          newPhotosByOriginal[sourceId].forEach((newPhoto) => {
            photos.push(newPhoto)
          })
          processedOriginalIds.add(sourceId)
        } else {
          // sourceId exists but no matching original found
          newPhotosByOriginal[sourceId].forEach((newPhoto) => {
            photos.push(newPhoto)
          })
          processedOriginalIds.add(sourceId)
        }
      })

      // Add photos without any source_id at the end
      photosWithoutOriginal.forEach((photo) => {
        photos.push(photo)
      })

      return photos
    },
    buildDynamicGallery() {
      let photos = this.photos
      if (this.reinspectionWithPhotos) {
        photos = this.photosWithOriginals()
      }
      return photos.map((record) => {
        return buildGalleryItem(record)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.card-photos {
  height: 11em;
  max-height: 11em;
  min-height: 11em;
  border: 1px solid #fff;
}

.flex {
  overflow: hidden;
  text-align: center;
  align-items: center;
  display: flex;
  justify-content: center;
}

.half {
  &:first-child {
    border-right: 1px solid #fff;
  }
  &:last-child {
    border-left: 1px solid #fff;
  }
}

.additional-photos {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-left: 2px solid #fff;

  .additional-photo {
    &:nth-child(1) {
      border-bottom: 1px solid #fff;
    }
    &:nth-child(2) {
      border-top: 1px solid #fff;
    }
    &:nth-child(3) {
      border-top: 2px solid #fff;
    }
  }
}

.more {
  width: 100%;
  color: #fff;
  background-color: #555;
  transition: opacity 0.1s linear;
  max-height: 2em;

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
}

.reinspection-photos {
  height: inherit;
  position: relative;

  > .layout {
    height: 100%;
  }

  .half {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    height: 100%;
    align-items: stretch;
    justify-content: flex-start;
  }
}

.reinspection-thumb {
  flex: 1;
  width: 100%;
  position: relative;
  background-size: cover;
  background-position: center;
  background-color: #f5f5f5;
}

.photo-count-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reinspection-label {
  font-size: 12px;
  font-weight: 400;
  text-align: center;
  padding: 2px;
  line-height: 19px;
  letter-spacing: -0.5px;
  color: #212731;
  background: #f6f7f8;
  flex-shrink: 0;

  &--reinspection {
    color: #1771b8;
    background: #f1f8fd;
  }
}
</style>

<style lang="scss">
.card-photos {
  img:not(.materialboxed) {
    max-width: 250%;
    max-height: 250%;
  }

  .additional-photo {
    width: 100%;
    height: 100%;
  }

  .photo-wrapper {
    height: inherit;
    width: inherit;
    position: relative;
    display: flex;
    justify-content: center;

    img {
      position: absolute;
      top: -9999px;
      bottom: -9999px;
      left: -9999px;
      right: -9999px;
      margin: auto;
    }
  }
}
</style>
